import type {
  CertificationState,
  SystemScenarioRunRecord,
} from '@/governor/system-workspaces/SystemWorkspaceContracts';

import {
  listTreeCorrectnessScenarios,
  listTreeCorrectnessSimulationRuns,
} from './TreeCorrectnessSimulatorRuntime';

import {
  listTreeScaleSimulationResults,
  type TreeScaleSimulationResult,
} from './TreeScaleSimulationEngine';

import {
  TREE_SCALE_SIMULATION_PROFILES,
  type TreeScaleProfileId,
} from './TreeScaleSimulationProfiles';

export type TreeSimulationCertificationStatus =
  | 'NOT_RUN'
  | 'PASS'
  | 'FAIL';

export type TreeSimulationCertificationProjection = {
  systemId: 'tree';
  status: TreeSimulationCertificationStatus;
  simulatedRuntime: CertificationState;
  correctness: {
    requiredScenarioCount: number;
    completedScenarioCount: number;
    passedScenarioCount: number;
    failedScenarioIds: string[];
    missingScenarioIds: string[];
  };
  scale: {
    requiredProfileCount: number;
    completedProfileCount: number;
    passedProfileCount: number;
    failedProfileIds: TreeScaleProfileId[];
    missingProfileIds: TreeScaleProfileId[];
    maximumTargetUserCount: number;
    millionUserProjectionPassed: boolean;
  };
  cleanupComplete: boolean;
  productionMutationDetected: boolean;
  simulatedCertificationAllowed: boolean;
  liveCertificationAllowed: false;
  reason: string;
  generatedAt: string;
};

function latestScenarioRuns(
  runs: SystemScenarioRunRecord[],
): Map<string, SystemScenarioRunRecord> {
  const latest = new Map<
    string,
    SystemScenarioRunRecord
  >();

  for (const run of runs) {
    if (!latest.has(run.scenarioId)) {
      latest.set(run.scenarioId, run);
    }
  }

  return latest;
}

function latestScaleResults(
  results: TreeScaleSimulationResult[],
): Map<
  TreeScaleProfileId,
  TreeScaleSimulationResult
> {
  const latest = new Map<
    TreeScaleProfileId,
    TreeScaleSimulationResult
  >();

  for (const result of results) {
    if (!latest.has(result.profileId)) {
      latest.set(
        result.profileId,
        result,
      );
    }
  }

  return latest;
}

export function projectTreeSimulationCertification():
  TreeSimulationCertificationProjection {
  const scenarios =
    listTreeCorrectnessScenarios();

  const scenarioRuns =
    latestScenarioRuns(
      listTreeCorrectnessSimulationRuns(),
    );

  const scaleResults =
    latestScaleResults(
      listTreeScaleSimulationResults(),
    );

  const missingScenarioIds =
    scenarios
      .filter(
        scenario =>
          !scenarioRuns.has(scenario.id),
      )
      .map(scenario => scenario.id);

  const failedScenarioIds =
    scenarios
      .filter(scenario => {
        const run =
          scenarioRuns.get(scenario.id);

        return Boolean(
          run &&
          (
            !run.passed ||
            !run.cleanupCompleted ||
            run.productionMutationOccurred
          ),
        );
      })
      .map(scenario => scenario.id);

  const passedScenarioCount =
    scenarios.filter(scenario => {
      const run =
        scenarioRuns.get(scenario.id);

      return Boolean(
        run?.passed &&
        run.cleanupCompleted &&
        !run.productionMutationOccurred,
      );
    }).length;

  const missingProfileIds =
    TREE_SCALE_SIMULATION_PROFILES
      .filter(
        profile =>
          !scaleResults.has(profile.id),
      )
      .map(profile => profile.id);

  const failedProfileIds =
    TREE_SCALE_SIMULATION_PROFILES
      .filter(profile => {
        const result =
          scaleResults.get(profile.id);

        return Boolean(
          result &&
          (
            result.state !== 'passed' ||
            !result.cleanupCompleted ||
            result.productionMutationOccurred
          ),
        );
      })
      .map(profile => profile.id);

  const passedProfileCount =
    TREE_SCALE_SIMULATION_PROFILES.filter(
      profile => {
        const result =
          scaleResults.get(profile.id);

        return Boolean(
          result?.state === 'passed' &&
          result.cleanupCompleted &&
          !result.productionMutationOccurred,
        );
      },
    ).length;

  const productionMutationDetected =
    [...scenarioRuns.values()].some(
      run =>
        run.productionMutationOccurred,
    ) ||
    [...scaleResults.values()].some(
      result =>
        result.productionMutationOccurred,
    );

  const cleanupComplete =
    [...scenarioRuns.values()].every(
      run => run.cleanupCompleted,
    ) &&
    [...scaleResults.values()].every(
      result =>
        result.cleanupCompleted,
    );

  const allRequiredCompleted =
    missingScenarioIds.length === 0 &&
    missingProfileIds.length === 0;

  const anyRequiredFailed =
    failedScenarioIds.length > 0 ||
    failedProfileIds.length > 0 ||
    productionMutationDetected ||
    !cleanupComplete;

  const simulatedCertificationAllowed =
    allRequiredCompleted &&
    !anyRequiredFailed;

  const status:
    TreeSimulationCertificationStatus =
      simulatedCertificationAllowed
        ? 'PASS'
        : anyRequiredFailed
          ? 'FAIL'
          : 'NOT_RUN';

  const simulatedRuntime:
    CertificationState =
      status === 'PASS'
        ? 'pass'
        : status === 'FAIL'
          ? 'fail'
          : 'not_run';

  const millionUserProjectionPassed =
    scaleResults.get('TREE_SCALE_1M')
      ?.state === 'passed' &&
    scaleResults.get('TREE_SCALE_1M')
      ?.cleanupCompleted === true &&
    scaleResults.get('TREE_SCALE_1M')
      ?.productionMutationOccurred ===
      false;

  const reason =
    status === 'PASS'
      ? 'All required Tree correctness and bounded scale simulations passed. Live certification remains blocked.'
      : status === 'FAIL'
        ? 'One or more required Tree simulations failed, did not clean up, or reported a prohibited mutation.'
        : 'Required Tree correctness and scale simulations have not all been run.';

  return {
    systemId: 'tree',
    status,
    simulatedRuntime,
    correctness: {
      requiredScenarioCount:
        scenarios.length,
      completedScenarioCount:
        scenarios.length -
        missingScenarioIds.length,
      passedScenarioCount,
      failedScenarioIds,
      missingScenarioIds,
    },
    scale: {
      requiredProfileCount:
        TREE_SCALE_SIMULATION_PROFILES.length,
      completedProfileCount:
        TREE_SCALE_SIMULATION_PROFILES.length -
        missingProfileIds.length,
      passedProfileCount,
      failedProfileIds,
      missingProfileIds,
      maximumTargetUserCount:
        Math.max(
          ...TREE_SCALE_SIMULATION_PROFILES.map(
            profile =>
              profile.targetUserCount,
          ),
        ),
      millionUserProjectionPassed,
    },
    cleanupComplete,
    productionMutationDetected,
    simulatedCertificationAllowed,
    liveCertificationAllowed: false,
    reason,
    generatedAt:
      new Date().toISOString(),
  };
}
